import React from "react";
import clsx from "clsx";
import { useCarConfigSelector } from "../../../../../redux/store";
import styles from "./SummaryBusinessLeasing.module.scss";
import summaryStyles from "./Summary.module.scss";
import LogoKinto from "../../../../../../../../../assets/toyota/svg/logo-kinto.svg";
import Text from "../../../../../../shared-components/css-modules/toyota/atoms/Text";
import Button from "../../../../../../shared-components/css-modules/toyota/atoms/Button";
import { useTabsContext } from "./SummaryTabsContext";
import type { SummaryTabPropsTypes } from "../../../../../hooks/useAphSummaryTabs";
import CtaButton from "../../../common/cta/CtaButton";
import { useAphBreakpoint } from "../../../../../../shared-logic/hooks/useBreakpoint";
import { CtaTarget } from "../../../../../../../shared-logic/hooks/useCtaTarget";
import SummaryServices from "./SummaryServices";
import SummaryManufacturerInfo from "./SummaryManufacturerInfo";

const shortKey = (key: string | undefined, index: number): string => {
    return `${key || "key"}-${index}`.toLowerCase();
};

export const SummaryBusinessLeasing: React.FC<SummaryTabPropsTypes> = (props) => {
    const tabsContext = useTabsContext();
    const businessLeasingConfig = useCarConfigSelector((state) => state.carConfigSettings.businessLeasing);
    const isMobile = useAphBreakpoint("down", "xl");

    if (!businessLeasingConfig) return null;

    const { tabId, tab } = props;
    const { title, description, disclaimer, listItems, infoLink, ctas } = businessLeasingConfig;
    const parsedCtas = (ctas && ctas[isMobile ? CtaTarget.Mobile : CtaTarget.Default]) || [];

    return (
        <section
            role="tabpanel"
            id={tabId}
            aria-labelledby={tab}
            className={clsx(styles.wrapper, summaryStyles["tab-panel"])}
            hidden={tabsContext.openTab !== "business"}
        >
            <header className={styles.header}>
                <div>
                    {title && (
                        <Text variant="apheleia-h4" color="highlight-black" spacing="spacing-0">
                            {title}
                        </Text>
                    )}
                    <figure className={styles.logo}>
                        <LogoKinto width="54" height="13" viewBox="0 0 481 124" />
                    </figure>
                </div>
            </header>
            {/* No danger, content is sanitized in formatter */}
            {/* eslint-disable-next-line react/no-danger */}
            {description && <div className={styles.description} dangerouslySetInnerHTML={{ __html: description }} />}
            {!!listItems && (
                <div className={styles.body}>
                    {listItems?.map((listCollection, listIndex) => (
                        <div key={`business-leasing-info-list-${shortKey(listCollection.title, listIndex)}`}>
                            {listCollection.title && (
                                <Text variant="apheleia-h5" tag="h3" className={styles.heading}>
                                    {listCollection.title}
                                </Text>
                            )}
                            {listCollection.list && listCollection.list.length && (
                                <ul>
                                    {listCollection.list.map((listItem, index, arr) => (
                                        <li
                                            key={`business-leasing-info-list-item-${shortKey(
                                                listCollection.title,
                                                listIndex,
                                            )}-${arr.length}-${shortKey(listItem, index)}`}
                                        >
                                            {listItem}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    ))}
                </div>
            )}
            {infoLink && infoLink.url && (
                <div>
                    <Button variant="btn-icon-text" href={infoLink.url} target={infoLink.target}>
                        {infoLink.urlLabel}
                    </Button>
                </div>
            )}
            <SummaryManufacturerInfo />
            {disclaimer && <div className={styles.disclaimer} dangerouslySetInnerHTML={{ __html: disclaimer }} />}
            {parsedCtas && parsedCtas.length > 0 && (
                <div className={styles.ctas}>
                    {parsedCtas.map((cta, index) => (
                        <CtaButton
                            // eslint-disable-next-line react/no-array-index-key
                            key={`business-leasing-cta-${index}`}
                            ctaConfig={cta}
                            sizing="full-width"
                        />
                    ))}
                </div>
            )}
            <SummaryServices />
        </section>
    );
};
